import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { API_BASE_URL } from '../../utils/apiClient';
import './Emularity.css';

/**
 * Emularity Emulator Component
 * 
 * This component handles MAME emulation using the Emularity loader framework.
 * It's designed to work with various vintage computing platforms that are supported by MAME.
 * 
 * Launch options can be specified in the game's launchoptions column using the format:
 * --driver=merlin --resolution=200x490 --scale=1.5 --aspectratio=2/3 --js=mamehh_tms1k.js --wasm=mamehh_tms1k.wasm
 * 
 * Available launch options:
 * --driver=<name>         : MAME driver to use (e.g., --driver=merlin)
 * --resolution=<w>x<h>    : Native resolution of the emulator (e.g., --resolution=200x490)
 * --scale=<factor>        : Scaling factor (e.g., --scale=1.5)
 * --aspectratio=<ratio>   : Aspect ratio as a fraction (e.g., --aspectratio=2/3)
 * --js=<filename>         : JavaScript file for emulator (e.g., --js=mamehh_tms1k.js)
 * --wasm=<filename>       : WebAssembly file for emulator (e.g., --wasm=mamehh_tms1k.wasm)
 */
const Emularity = ({ game, selectedRomVersion }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const iframeRef = useRef(null);
  const containerRef = useRef(null);
  const [emularityConfig, setEmularityConfig] = useState({});

  // Helper function for debug messages
  const addDebugMessage = (message) => {
    console.log('Emularity Emulator Debug:', message);
  };
  
  // Build the iframe URL with all configuration parameters
  const buildEmularityUrl = useCallback((config = emularityConfig) => {
    const baseUrl = '/emularity/emularity.html';
    const queryString = new URLSearchParams();
    
    queryString.append('driver', config.driver);
    queryString.append('width', config.resolution.width);
    queryString.append('height', config.resolution.height);
    queryString.append('scale', config.scale);
    queryString.append('aspectRatio', config.aspectRatio);
    queryString.append('js', config.js);
    queryString.append('wasm', config.wasm);
    queryString.append('rom', config.romUrl);
    queryString.append('romName', config.romFilename);
    
    return `${baseUrl}?${queryString.toString()}`;
  }, [emularityConfig]);
  
  // Parse launch options from game object
  const parseLaunchOptions = useCallback((launchOptions) => {
    if (!launchOptions) return { 
      driver: 'merlin',
      resolution: { width: 200, height: 490 },
      scale: 1.5,
      aspectRatio: '2/3',
      js: 'mamehh_tms1k.js',
      wasm: 'mamehh_tms1k.wasm'
    };
    
    const options = {};
    
    // Parse driver setting
    const driverMatch = launchOptions.match(/--driver=([^\s]+)/);
    if (driverMatch && driverMatch[1]) {
      options.driver = driverMatch[1];
      addDebugMessage(`Using driver: ${options.driver}`);
    }
    
    // Parse resolution setting
    const resolutionMatch = launchOptions.match(/--resolution=(\d+)x(\d+)/);
    if (resolutionMatch && resolutionMatch[1] && resolutionMatch[2]) {
      options.resolution = {
        width: parseInt(resolutionMatch[1], 10),
        height: parseInt(resolutionMatch[2], 10)
      };
      addDebugMessage(`Setting resolution to: ${options.resolution.width}x${options.resolution.height}`);
    }
    
    // Parse scale setting
    const scaleMatch = launchOptions.match(/--scale=([^\s]+)/);
    if (scaleMatch && scaleMatch[1]) {
      options.scale = parseFloat(scaleMatch[1]);
      addDebugMessage(`Using scale factor: ${options.scale}`);
    }
    
    // Parse aspect ratio setting
    const aspectRatioMatch = launchOptions.match(/--aspectratio=([^\s]+)/);
    if (aspectRatioMatch && aspectRatioMatch[1]) {
      options.aspectRatio = aspectRatioMatch[1];
      addDebugMessage(`Using aspect ratio: ${options.aspectRatio}`);
    }
    
    // Parse JS file setting
    const jsMatch = launchOptions.match(/--js=([^\s]+)/);
    if (jsMatch && jsMatch[1]) {
      options.js = jsMatch[1];
      addDebugMessage(`Using JS file: ${options.js}`);
    }
    
    // Parse WASM file setting
    const wasmMatch = launchOptions.match(/--wasm=([^\s]+)/);
    if (wasmMatch && wasmMatch[1]) {
      options.wasm = wasmMatch[1];
      addDebugMessage(`Using WASM file: ${options.wasm}`);
    }
    
    return options;
  }, []);
  
  // Calculate the optimal scale based on container size and emulator resolution
  const calculateOptimalScale = useCallback((resolution, containerSize) => {
    if (!resolution || !containerSize) return 1.5; // Default scale
    
    // Get the available width and height
    const availableWidth = containerSize.width;
    const availableHeight = containerSize.height;
    
    // Calculate how much we can scale the emulator to fit the container
    const widthScale = availableWidth / resolution.width;
    const heightScale = availableHeight / resolution.height;
    
    // Use the smaller scale to ensure the emulator fits in the container
    // This preserves the original aspect ratio
    let optimalScale = Math.min(widthScale, heightScale);
    
    // Ensure the scale isn't too small or too large
    optimalScale = Math.max(0.5, Math.min(optimalScale, 4));
    
    // Round to 2 decimal places
    optimalScale = Math.round(optimalScale * 100) / 100;
    
    addDebugMessage(`Calculated optimal scale: ${optimalScale} (width scale: ${widthScale}, height scale: ${heightScale})`);
    
    return optimalScale;
  }, []);
  
  // Measure the container size to calculate optimal scale
  const measureContainer = useCallback(() => {
    if (!containerRef.current) return null;
    
    const rect = containerRef.current.getBoundingClientRect();
    addDebugMessage(`Container size: ${rect.width}x${rect.height}`);
    
    return {
      width: rect.width,
      height: rect.height
    };
  }, []);
  
  useEffect(() => {
    const setupEmulator = async () => {
      try {
        setLoading(true);
        addDebugMessage('Setting up Emularity emulator');
        
        // Use the ROM path already selected in the game object
        const romToUse = game.rom;
          
        if (!romToUse) {
          throw new Error('No ROM specified for Emularity emulator');
        }
        
        const gameId = game.id;
        
        addDebugMessage(`Using ROM: ${romToUse}`);
        
        // Parse launch options from game object
        const launchOptions = parseLaunchOptions(game.launchoptions);
        
        // Determine ROM path based on loadasname, similar to EmulatorJS
        let romPath;
        let romFilename;
        
        // Check if game has loadasname
        if (game.loadasname) {
          const loadasname = game.loadasname;
          const extPos = loadasname.lastIndexOf('.');
          
          // Check if we need to add a suffix based on the version
          if (selectedRomVersion === 'japanese' || selectedRomVersion === 'version-1') {
            // Create the Japanese ROM loadasname by adding _jpn before the extension
            if (extPos !== -1) {
              romFilename = `${loadasname.substring(0, extPos)}_jpn${loadasname.substring(extPos)}`;
            } else {
              romFilename = `${loadasname}_jpn`;
            }
          } else if (selectedRomVersion === 'chinese' || selectedRomVersion === 'version-2') {
            // Create the Chinese ROM loadasname by adding _chn before the extension
            if (extPos !== -1) {
              romFilename = `${loadasname.substring(0, extPos)}_chn${loadasname.substring(extPos)}`;
            } else {
              romFilename = `${loadasname}_chn`;
            }
          } else if (selectedRomVersion === 'multiplayer') {
            // Create the Multiplayer ROM loadasname by adding _mp before the extension
            if (extPos !== -1) {
              romFilename = `${loadasname.substring(0, extPos)}_mp${loadasname.substring(extPos)}`;
            } else {
              romFilename = `${loadasname}_mp`;
            }
          } else {
            // Original ROM path
            romFilename = loadasname;
          }
          
          // Use temp path with game id and loadasname
          romPath = `/roms/temp/${gameId}/${romFilename}`;
          addDebugMessage(`Using temp ROM path with loadasname: ${romPath}`);
        } else {
          // Use the direct ROM path if no loadasname is specified
          romPath = romToUse;
          romFilename = romToUse.split('/').pop();
          addDebugMessage(`Using direct ROM path: ${romPath}`);
        }
        
        // Measure container to calculate optimal scale
        const containerSize = measureContainer();
        let optimalScale = launchOptions.scale || 1.5; // Use provided scale or default
        
        if (containerSize && launchOptions.resolution) {
          // Calculate optimal scale based on container and resolution
          optimalScale = calculateOptimalScale(launchOptions.resolution, containerSize);
        }
        
        // Save the configuration for the iframe
        setEmularityConfig({
          driver: launchOptions.driver || 'merlin',
          resolution: launchOptions.resolution || { width: 200, height: 490 },
          scale: optimalScale, // Use the calculated optimal scale
          aspectRatio: launchOptions.aspectRatio || '2/3',
          js: launchOptions.js || 'mamehh_tms1k.js',
          wasm: launchOptions.wasm || 'mamehh_tms1k.wasm',
          romUrl: `${API_BASE_URL}${romPath}`, // No longer prepending /roms/
          romFilename: romFilename
        });
        
        setLoading(false);
        addDebugMessage('Emularity setup completed successfully');
        addDebugMessage(`Final ROM URL: ${API_BASE_URL}${romPath}`);
        addDebugMessage(`Using scale: ${optimalScale}`);
      } catch (err) {
        console.error('Error setting up Emularity emulator:', err);
        setError(`Failed to set up Emularity emulator: ${err.message}`);
        setLoading(false);
        addDebugMessage(`Error: ${err.message}`);
      }
    };
    
    setupEmulator();
    
    // Add resize handler to recalculate scale when window is resized
    const handleResize = () => {
      const containerSize = measureContainer();
      if (containerSize && emularityConfig.resolution) {
        const newScale = calculateOptimalScale(emularityConfig.resolution, containerSize);
        if (newScale !== emularityConfig.scale) {
          setEmularityConfig(prev => ({
            ...prev,
            scale: newScale
          }));
          
          addDebugMessage(`Window resized, new scale: ${newScale}`);
          
          // Reload the iframe to apply the new scale
          if (iframeRef.current) {
            iframeRef.current.src = buildEmularityUrl({
              ...emularityConfig,
              scale: newScale
            });
          }
        }
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [game, selectedRomVersion, buildEmularityUrl, calculateOptimalScale, emularityConfig, measureContainer, parseLaunchOptions]);
  
  if (loading) {
    return (
      <div className="emularity-loading">
        <div className="loading-message">Loading Emularity emulator...</div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="emularity-error">
        <div className="error-message">{error}</div>
      </div>
    );
  }
  
  return (
    <div className="emularity-container" ref={containerRef}>
      <iframe
        ref={iframeRef}
        src={buildEmularityUrl()}
        title={game.titleeng || game.titlechn}
        className="emularity-frame emulator-frame"
        sandbox="allow-same-origin allow-scripts allow-forms allow-modals allow-pointer-lock"
        allow="autoplay; fullscreen; pointer-lock"
        allowFullScreen
      />
    </div>
  );
};

Emularity.propTypes = {
  game: PropTypes.object.isRequired,
  selectedRomVersion: PropTypes.string.isRequired
};

export default Emularity; 